import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

// ─── Konstanta ────────────────────────────────────────────────
const TOTAL_COLS    = 40;
const ROWS_PER_COL = 51;
const ANSWERS_PER_COL = ROWS_PER_COL - 1;
const COL_TIME      = 90; // detik per kolom (standar baku)

const STORAGE_KEY_STATISTIK = 'kraepelin_statistik';
const STANDAR_BAKU_PER_3_MENIT = 182; // nilai baku pusat: minimal 182 hitungan tiap 3 menit

// Hitung total benar/salah/dikerjakan dari kumpulan kolom jawaban terhadap sheet soal.
// Dipakai baik saat checkpoint (progress berjalan) maupun saat tes selesai, supaya
// logika penilaian selalu satu sumber (tidak ada duplikasi rumus yang bisa beda sendiri-sendiri).
function hitungBenarSalah(semuaKolom, sheet) {
  let benar = 0, dikerjakan = 0;
  semuaKolom.forEach((col, ci) => {
    const sheetCol = sheet[ci % TOTAL_COLS]; // "bolak-balik" bisa ngulang kolom, sheet-nya sama
    col.forEach((ans, ri) => {
      if (ans !== undefined) {
        dikerjakan++;
        // ri = indeks penyimpanan jawaban (colAnswers[ri]). Baris digit yang sebenarnya
        // dipasangkan dengan slot ri adalah (ANSWERS_PER_COL-1-ri) — BUKAN ri itu sendiri.
        const rowActual = ANSWERS_PER_COL - 1 - ri;
        const correct = (sheetCol[rowActual] + sheetCol[rowActual + 1]) % 10;
        if (ans === correct) benar++;
      }
    });
  });
  return { benar, salah: dikerjakan - benar, dikerjakan };
}

// ─── Helper ───────────────────────────────────────────────────
function formatTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

function generateSheet(tipe = 'acak') {
  const sheet = [];
  for (let c = 0; c < TOTAL_COLS; c++) {
    const col = [];
    for (let r = 0; r < ROWS_PER_COL; r++) {
      if (tipe === 'berurut') {
        col.push((r % 10));
      } else {
        col.push(Math.floor(Math.random() * 10));
      }
    }
    sheet.push(col);
  }
  return sheet;
}

// ─── Simpan & Ambil Statistik ─────────────────────────────────
function simpanStatistik(data) {
  try {
    const existing = ambilStatistik();
    existing.push(data);
    localStorage.setItem(STORAGE_KEY_STATISTIK, JSON.stringify(existing));
  } catch {}
}

function ambilStatistik() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_STATISTIK);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

// Banner "lanjutkan pengerjaan sebelumnya" — kolom & waktu dari progres tersimpan.
function BannerLanjutkanKraepelin({ progresAwal, onLanjutkan, onMulaiBaru }) {
  if (!progresAwal) return null;
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '2px solid var(--accent)' }}>
      <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: 'var(--accent)' }}>⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm mb-3" style={{ color: 'var(--text-primary)' }}>
        Baru sampai kolom <b>{(progresAwal.currentCol || 0) + 1}</b>, sisa waktu <b>{formatTime(progresAwal.timeLeft || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan}
          className="flex-1 py-2 rounded-lg text-sm font-bold"
          style={{ backgroundColor: 'var(--accent)', color: '#1B2017' }}>
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru}
          className="flex-1 py-2 rounded-lg border text-sm font-semibold"
          style={{ borderColor: 'var(--accent)', color: 'var(--text-primary)' }}>
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Pengaturan ─────────────────────────────────────────
function LayarPengaturan({ onMulai, onBack, onLihatStatistik, progresAwal, onLanjutkan, onMulaiBaru }) {
  const [waktu, setWaktu] = useState(30);
  const [waktuCustom, setWaktuCustom] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [tipeSoal, setTipeSoal] = useState('acak');
  const [formatSoal, setFormatSoal] = useState('vertikal');
  const [layoutKeyboard, setLayoutKeyboard] = useState('grid');

  const PILIHAN_WAKTU = [1, 2, 5, 11.5, 22.5, 30];

  const handleMulai = () => {
    const waktuFinal = isCustom ? parseFloat(waktuCustom) || 30 : waktu;
    onMulai({ waktuMenit: waktuFinal, tipeSoal, formatSoal, layoutKeyboard });
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto"
         style={{ backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex-shrink-0"
           style={{ backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-3"
                style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={16} /> Kembali
        </button>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)' }}>
          KRAEPELIN / PAULI
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Atur pengaturan tes sebelum mulai</p>
      </div>

      <div className="flex-1 px-5 py-4 space-y-5">
        <BannerLanjutkanKraepelin progresAwal={progresAwal} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        {/* Waktu */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
            Durasi Tes
          </p>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {PILIHAN_WAKTU.map(w => (
              <button key={w} onClick={() => { setWaktu(w); setIsCustom(false); }}
                className="py-2 rounded-lg text-sm font-semibold transition"
                style={{
                  backgroundColor: !isCustom && waktu === w ? 'var(--accent)' : 'var(--bg-card3)',
                  color: !isCustom && waktu === w ? '#1B2017' : 'var(--text-primary)',
                  border: '1px solid var(--border)'
                }}>
                {w} menit
              </button>
            ))}
          </div>
          <button onClick={() => setIsCustom(true)}
            className="w-full py-2 rounded-lg text-sm font-semibold transition"
            style={{
              backgroundColor: isCustom ? 'var(--accent)' : 'var(--bg-card3)',
              color: isCustom ? '#1B2017' : 'var(--text-primary)',
              border: '1px solid var(--border)'
            }}>
            Isi Sendiri
          </button>
          {isCustom && (
            <input type="number" placeholder="Masukkan menit..."
              value={waktuCustom} onChange={e => setWaktuCustom(e.target.value)}
              className="w-full mt-2 px-3 py-2 rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-primary)', border: '1px solid var(--accent)' }}
            />
          )}
        </div>

        {/* Tipe Soal */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
            Tipe Soal
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[['berurut', 'Berurut'], ['acak', 'Acak']].map(([val, label]) => (
              <button key={val} onClick={() => setTipeSoal(val)}
                className="py-2.5 rounded-lg text-sm font-semibold transition"
                style={{
                  backgroundColor: tipeSoal === val ? 'var(--accent)' : 'var(--bg-card3)',
                  color: tipeSoal === val ? '#1B2017' : 'var(--text-primary)',
                  border: '1px solid var(--border)'
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Format Soal */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
            Format Soal
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[['vertikal', 'Vertikal'], ['horizontal', 'Horizontal']].map(([val, label]) => (
              <button key={val} onClick={() => setFormatSoal(val)}
                className="py-2.5 rounded-lg text-sm font-semibold transition"
                style={{
                  backgroundColor: formatSoal === val ? 'var(--accent)' : 'var(--bg-card3)',
                  color: formatSoal === val ? '#1B2017' : 'var(--text-primary)',
                  border: '1px solid var(--border)'
                }}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
            Vertikal: angka tersusun dari atas ke bawah (format standar baku)
          </p>
        </div>

        {/* Layout Keyboard */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
            Layout Keyboard
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[['row', 'Baris (1-5 / 6-0)'], ['grid', 'Grid 3×3 + 0']].map(([val, label]) => (
              <button key={val} onClick={() => setLayoutKeyboard(val)}
                className="py-2.5 rounded-lg text-sm font-semibold transition"
                style={{
                  backgroundColor: layoutKeyboard === val ? 'var(--accent)' : 'var(--bg-card3)',
                  color: layoutKeyboard === val ? '#1B2017' : 'var(--text-primary)',
                  border: '1px solid var(--border)'
                }}>
                {label}
              </button>
            ))}
          </div>
          {/* Preview keyboard */}
          <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-card3)' }}>
            {layoutKeyboard === 'row' ? (
              <div className="space-y-1">
                <div className="grid grid-cols-5 gap-1">
                  {[1,2,3,4,5].map(d => <div key={d} className="h-7 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)' }}>{d}</div>)}
                </div>
                <div className="grid grid-cols-5 gap-1">
                  {[6,7,8,9,0].map(d => <div key={d} className="h-7 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)' }}>{d}</div>)}
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-3 gap-1">
                  {[1,2,3,4,5,6,7,8,9].map(d => <div key={d} className="h-7 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)' }}>{d}</div>)}
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <div/>
                  <div className="h-7 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)' }}>0</div>
                  <div/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tombol aksi */}
      <div className="px-5 pb-5 pt-2 space-y-2 flex-shrink-0">
        <button onClick={handleMulai}
          className="w-full py-3.5 rounded-xl font-bold text-sm uppercase tracking-wide"
          style={{ backgroundColor: 'var(--accent)', color: '#1B2017', fontFamily: "'Poppins', sans-serif" }}>
          Mulai Tes →
        </button>
        <button onClick={onLihatStatistik}
          className="w-full py-3 rounded-xl font-semibold text-sm"
          style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
          📊 Lihat Statistik Riwayat
        </button>
      </div>
    </div>
  );
}

// ─── Layar Statistik ──────────────────────────────────────────
function LayarStatistik({ onBack }) {
  const data = ambilStatistik();
  const [sesiDibuka, setSesiDibuka] = useState(null); // index sesi (dari data terurut lama→baru) yang lagi dibuka detailnya

  const W = 300, H = 120, PAD = 16;
  const skorList = data.map(d => d.akurasi);
  const maks = Math.max(100, ...skorList);

  const points = skorList.length >= 2
    ? skorList.map((s, i) => {
        const x = PAD + (i / (skorList.length - 1)) * (W - PAD * 2);
        const y = H - PAD - (s / maks) * (H - PAD * 2);
        return `${x},${y}`;
      }).join(' ')
    : null;

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' }}>
      <div className="px-5 pt-5 pb-4 flex-shrink-0"
           style={{ backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={onBack} className="flex items-center gap-2 text-sm mb-3"
                style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={16} /> Kembali
        </button>
        <h2 className="text-xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
          STATISTIK ({data.length} sesi)
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {data.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-4xl mb-3">📊</p>
            <p style={{ color: 'var(--text-muted)' }}>Belum ada data statistik.</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Selesaikan tes pertama untuk melihat statistik.</p>
          </div>
        ) : (
          <>
            {/* Grafik tren */}
            {points && (
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
                  Tren Akurasi
                </p>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 120 }}>
                  <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke="var(--border)" strokeWidth="1"/>
                  <line x1={PAD} y1={PAD} x2={PAD} y2={H-PAD} stroke="var(--border)" strokeWidth="1"/>
                  <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  {skorList.map((s, i) => {
                    const x = PAD + (i / (skorList.length - 1)) * (W - PAD * 2);
                    const y = H - PAD - (s / maks) * (H - PAD * 2);
                    return <circle key={i} cx={x} cy={y} r="4" fill="var(--accent)"/>;
                  })}
                </svg>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--accent)' }}/>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Akurasi (%)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tabel */}
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              <div className="px-4 py-2" style={{ backgroundColor: 'var(--bg-card2)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>
                  Detail Per Sesi
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  *Benar = jawaban benar | **Akurasi = % dari yang dikerjakan
                </p>
              </div>
              <div style={{ backgroundColor: 'var(--bg-card3)' }}>
                {/* Header */}
                <div className="grid text-[10px] font-bold uppercase px-3 py-2"
                     style={{ gridTemplateColumns: '24px 1fr 48px 48px 48px 52px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                  <span>No</span>
                  <span>Tanggal</span>
                  <span className="text-center">Menit</span>
                  <span className="text-center">Benar</span>
                  <span className="text-center">Kerjakan</span>
                  <span className="text-center">Akurasi</span>
                </div>
                {/* Rows */}
                {[...data].reverse().map((d, i) => {
                  const tgl = new Date(d.tanggal);
                  const tglStr = `${tgl.getDate()}/${tgl.getMonth()+1} ${tgl.getHours()}:${String(tgl.getMinutes()).padStart(2,'0')}`;
                  const terbuka = sesiDibuka === i;
                  return (
                    <div key={i}>
                      <button
                        onClick={() => setSesiDibuka(terbuka ? null : i)}
                        className="w-full grid text-xs px-3 py-2.5 text-left"
                        style={{ gridTemplateColumns: '24px 1fr 48px 48px 48px 52px 16px', backgroundColor: terbuka ? 'var(--bg-card2)' : 'transparent', borderBottom: (i < data.length-1 && !terbuka) ? '1px solid var(--border)' : 'none', color: 'var(--text-primary)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{data.length - i}</span>
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{tglStr}</span>
                        <span className="text-center">{d.waktuMenit}</span>
                        <span className="text-center font-semibold" style={{ color: 'var(--success)' }}>{d.totalBenar}</span>
                        <span className="text-center">{d.totalDikerjakan}</span>
                        <span className="text-center font-bold" style={{ color: 'var(--accent)' }}>{d.akurasi}%</span>
                        <span className="text-center" style={{ color: 'var(--text-muted)', fontSize: 10 }}>{terbuka ? '▲' : '▼'}</span>
                      </button>
                      {terbuka && (
                        <div className="px-3 pb-4 pt-1 space-y-3" style={{ backgroundColor: 'var(--bg-card2)', borderBottom: i < data.length-1 ? '1px solid var(--border)' : 'none' }}>
                          <KartuTempoKerja rasioTempo={d.rasioTempo} tempoPerTigaMenit={d.tempoPerTigaMenit} waktuMenit={d.waktuMenit} />
                          <GrafikKecepatanCheckpoint grafikProgress={d.grafikProgress} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ringkasan */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Rata-rata Akurasi', value: `${Math.round(data.reduce((a,d) => a + d.akurasi, 0) / data.length)}%`, color: 'var(--accent)' },
                { label: 'Total Sesi', value: data.length, color: 'var(--info)' },
                { label: 'Akurasi Terbaik', value: `${Math.max(...data.map(d => d.akurasi))}%`, color: 'var(--success)' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                  <p className="text-lg font-extrabold" style={{ color: item.color, fontFamily: "'JetBrains Mono', monospace" }}>{item.value}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Komponen Numpad ──────────────────────────────────────────
function Numpad({ layout, onPress }) {
  const btnStyle = {
    backgroundColor: 'var(--bg-card2)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    fontFamily: "'JetBrains Mono', monospace"
  };

  if (layout === 'grid') {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <button key={d} onClick={() => onPress(d)}
              className="h-14 rounded-xl text-xl font-bold active:scale-95 transition-all"
              style={btnStyle}>{d}</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div/>
          <button onClick={() => onPress(0)}
            className="h-14 rounded-xl text-xl font-bold active:scale-95 transition-all"
            style={btnStyle}>0</button>
          <div/>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-5 gap-2">
        {[1,2,3,4,5].map(d => (
          <button key={d} onClick={() => onPress(d)}
            className="h-12 rounded-xl text-xl font-bold active:scale-95 transition-all"
            style={btnStyle}>{d}</button>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {[6,7,8,9,0].map(d => (
          <button key={d} onClick={() => onPress(d)}
            className="h-12 rounded-xl text-xl font-bold active:scale-95 transition-all"
            style={btnStyle}>{d}</button>
        ))}
      </div>
    </div>
  );
}

// null (hasil JSON round-trip lewat localStorage) berarti "belum dijawab" —
// harus dikembalikan jadi `undefined` supaya seluruh logika penilaian &
// render (yang mengecek `!== undefined`) tetap benar setelah resume.
function desanitizeKolom(arr) {
  return (arr || []).map(v => (v === null ? undefined : v));
}

// ─── Layar Tes ────────────────────────────────────────────────
function LayarTes({ pengaturan, onSelesai, onBack, tryoutMode, tryoutId, resumeData }) {
  const { waktuMenit, tipeSoal, formatSoal, layoutKeyboard } = pengaturan;
  const TOTAL_WAKTU = Math.round(waktuMenit * 60);

  const [sheet]     = useState(() => resumeData?.sheet || generateSheet(tipeSoal));
  const [currentCol, setCurrentCol] = useState(() => resumeData?.currentCol ?? 0);
  const [currentRow, setCurrentRow] = useState(() => resumeData?.currentRow ?? (ANSWERS_PER_COL - 1));
  const [colAnswers, setColAnswers]  = useState(() => resumeData ? desanitizeKolom(resumeData.colAnswers) : Array(ANSWERS_PER_COL).fill(undefined));
  const [allAnswers, setAllAnswers]  = useState(() => resumeData ? (resumeData.allAnswers || []).map(desanitizeKolom) : []);
  const [inputVal, setInputVal]     = useState('');
  const [colTimeLeft, setColTimeLeft] = useState(() => resumeData?.colTimeLeft ?? COL_TIME);
  const [timeLeft, setTimeLeft]       = useState(() => resumeData?.timeLeft ?? TOTAL_WAKTU);
  const [grafikProgress, setGrafikProgress] = useState(() => resumeData?.grafikProgress || []); // {menit, jumlah} tiap checkpoint 3 menit
  const colRef  = useRef(null);
  const activeRef = useRef(null);

  // Autosave berkala selama mengerjakan — supaya kalau keluar tanpa sengaja
  // (koneksi putus, app ke-background, dsb), progres bisa dilanjutkan dari
  // titik ini, bukan mengulang dari kolom 1.
  useAutosaveTes(true, 'kraepelin', tryoutId, {
    pengaturan, sheet, currentCol, currentRow, colAnswers, allAnswers, timeLeft, colTimeLeft, grafikProgress,
  });

  // Ref yang selalu menunjuk ke versi TERBARU handleSelesai/pindahKolom — supaya timer
  // (yang cuma dipasang sekali di bawah) tidak memakai closure basi yang masih membawa
  // jawaban kosong dari render pertama. Tanpa ini, waktu habis akan menganggap user
  // belum menjawab apa-apa meski sebenarnya sudah mengerjakan banyak soal.
  const handleSelesaiRef = useRef(null);
  const pindahKolomRef = useRef(null);
  const catatCheckpointRef = useRef(null);

  // Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(interval); handleSelesaiRef.current(); return 0; }
        const elapsed = TOTAL_WAKTU - (prev - 1);
        // Checkpoint tiap 3 menit — catat berapa soal yang sudah dikerjakan (+ benar/salah) sejauh ini
        if (elapsed > 0 && elapsed % 180 === 0) {
          catatCheckpointRef.current(elapsed / 60);
        }
        return prev - 1;
      });
      setColTimeLeft(prev => {
        if (prev <= 1) { pindahKolomRef.current(); return COL_TIME; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto scroll ke active. Normalnya (jawab per baris dalam kolom yang sama)
  // pakai animasi smooth karena jaraknya cuma 1 baris, terasa wajar. TAPI pas
  // pindah kolom (colSwitchRef), lompatannya jauh (dari baris teratas kolom lama
  // balik ke baris terbawah kolom baru) — kalau tetap pakai smooth, kelihatan
  // "meluncur" cepat dan mengagetkan/memecah fokus (keluhan user). Makanya
  // khusus saat pindah kolom, scroll dibuat instan (auto) — posisinya tetap
  // berpindah tiba-tiba sesuai standar tes Kraepelin (kolom TETAP ganti paksa
  // tiap 90 detik, itu bagian dari tesnya), cuma transisi visualnya yang tidak
  // lagi "terbang" dari atas ke bawah layar.
  const colSwitchRef = useRef(false);
  useEffect(() => {
    if (activeRef.current) {
      const instan = colSwitchRef.current;
      activeRef.current.scrollIntoView({ block: 'center', behavior: instan ? 'auto' : 'smooth' });
      colSwitchRef.current = false;
    }
  }, [currentRow]);

  const pindahKolom = useCallback(() => {
    setAllAnswers(prev => [...prev, colAnswers]);
    setColAnswers(Array(ANSWERS_PER_COL).fill(undefined));
    // "Bolak-balik": kalau sudah sampai kolom terakhir tapi waktu total belum habis,
    // nomor kolom TERUS NAIK (41, 42, ...) — bukan reset ke 1 — biar peserta merasa terus
    // maju. Soal yang ditampilkan memang mengulang sheet yang sama (index % TOTAL_COLS),
    // tapi jumlah yang dikerjakan/dihitung tetap terus bertambah, tidak direset.
    colSwitchRef.current = true;
    setCurrentCol(prev => prev + 1);
    setCurrentRow(ANSWERS_PER_COL - 1);
    setInputVal('');
  }, [colAnswers]);

  const handleNumpad = useCallback((digit) => {
    const userAns = parseInt(digit);

    setColAnswers(prev => {
      const next = [...prev];
      next[currentRow] = userAns;
      return next;
    });
    setInputVal(String(digit));

    setTimeout(() => {
      setInputVal('');
      if (currentRow > 0) {
        setCurrentRow(prev => prev - 1);
      } else {
        pindahKolom();
      }
    }, 150);
  }, [currentRow, pindahKolom]);

  const handleSelesai = useCallback(() => {
    const semua = [...allAnswers, colAnswers];
    const { benar, salah, dikerjakan } = hitungBenarSalah(semua, sheet);
    const akurasi = dikerjakan === 0 ? 0 : Math.round((benar / dikerjakan) * 100);
    const totalMenit = TOTAL_WAKTU / 60;
    // Rasio tempo terhadap nilai baku pusat (182 hitungan / 3 menit), dinormalisasi
    // supaya adil dibandingkan berapa pun durasi tes yang dipilih user.
    const tempoPerTigaMenit = totalMenit > 0 ? dikerjakan / (totalMenit / 3) : 0;
    const rasioTempo = Math.round((tempoPerTigaMenit / STANDAR_BAKU_PER_3_MENIT) * 100);
    const grafikFinal = [...grafikProgress, { menit: Math.round(totalMenit), jumlah: dikerjakan, benar, salah }];
    const hasilData = {
      tanggal: new Date().toISOString(),
      waktuMenit,
      totalBenar: benar,
      totalSalah: salah,
      totalDikerjakan: dikerjakan,
      akurasi,
      tempoPerTigaMenit: Math.round(tempoPerTigaMenit),
      rasioTempo,
      standarBaku: STANDAR_BAKU_PER_3_MENIT,
      grafikProgress: grafikFinal,
    };
    simpanStatistik(hasilData);
    hapusProgresTes('kraepelin', tryoutId);
    catatHasilTes('kraepelin', akurasi, hasilData, tryoutMode, tryoutId);
    onSelesai(hasilData, sheet, semua);
  }, [allAnswers, colAnswers, sheet, waktuMenit, onSelesai, grafikProgress]);

  // Checkpoint tiap 3 menit: catat jumlah dikerjakan + benar/salah SEJAUH INI (kumulatif),
  // bukan cuma jumlah mentah — supaya grafik hasil bisa menunjukkan benar/salah per titik,
  // bukan cuma total soal yang disentuh.
  const catatCheckpoint = useCallback((menitKe) => {
    const semua = [...allAnswers, colAnswers];
    const { benar, salah, dikerjakan } = hitungBenarSalah(semua, sheet);
    setGrafikProgress(g => [...g, { menit: menitKe, jumlah: dikerjakan, benar, salah }]);
  }, [allAnswers, colAnswers, sheet]);

  // Perbarui ref setiap render supaya timer di atas selalu memanggil versi terbaru
  useEffect(() => {
    handleSelesaiRef.current = handleSelesai;
    pindahKolomRef.current = pindahKolom;
    catatCheckpointRef.current = catatCheckpoint;
  }, [handleSelesai, pindahKolom, catatCheckpoint]);

  const col = sheet[currentCol % TOTAL_COLS];
  const colProgress = ((COL_TIME - colTimeLeft) / COL_TIME) * 100;
  const totalProgress = ((TOTAL_WAKTU - timeLeft) / TOTAL_WAKTU) * 100;

  return (
    <div className="flex flex-col" style={{ height: '100%', backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' }}>

      {/* Top bar */}
      <div className="px-4 pt-4 pb-2 flex-shrink-0"
           style={{ backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Kolom</p>
            <p className="text-lg font-bold leading-none" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent)' }}>
              {currentCol + 1}
              {currentCol >= TOTAL_COLS && (
                <span className="text-[10px] ml-1 align-top" style={{ color: 'var(--accent)' }}>🔁 putaran {Math.floor(currentCol / TOTAL_COLS) + 1}</span>
              )}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Waktu Kolom</p>
            <p className={`text-xl font-bold`}
               style={{ fontFamily: "'JetBrains Mono', monospace", color: colTimeLeft <= 15 ? 'var(--danger)' : 'var(--text-primary)' }}>
              {formatTime(colTimeLeft)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Total</p>
            <p className="text-lg font-bold leading-none"
               style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-primary)' }}>
              {formatTime(timeLeft)}
            </p>
          </div>
        </div>
        <div className="space-y-1">
          <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${colProgress}%`, backgroundColor: 'var(--accent)' }}/>
          </div>
          <div className="h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${totalProgress}%`, backgroundColor: 'var(--text-muted)' }}/>
          </div>
        </div>
      </div>

      {/* Soal */}
      <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0" ref={colRef}>
        {formatSoal === 'vertikal' ? (
          <div className="max-w-xs mx-auto">
            {Array.from({ length: ROWS_PER_COL }).map((_, rowIdx) => {
              const isLastRow = rowIdx === ROWS_PER_COL - 1;
              const jawabanIdx = rowIdx;
              const isActive = (ANSWERS_PER_COL - 1 - jawabanIdx) === currentRow;
              const userAns = colAnswers[ANSWERS_PER_COL - 1 - jawabanIdx];
              const rowActual = ROWS_PER_COL - 2 - (ANSWERS_PER_COL - 1 - jawabanIdx);
              const correctAns = (col[rowActual] + col[rowActual + 1]) % 10;
              const isCorrect = userAns === correctAns;

              return (
                <React.Fragment key={rowIdx}>
                  <div className={`flex items-center justify-center h-8 rounded`}
                       style={{ backgroundColor: isActive ? 'var(--bg-card3)' : 'transparent' }}>
                    <span className="text-2xl font-bold"
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: isActive ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {col[rowIdx]}
                    </span>
                  </div>
                  {!isLastRow && (
                    <div ref={isActive ? activeRef : null}
                      className="flex items-center justify-center h-7 mx-8 rounded"
                      style={{
                        border: `1px solid ${isActive ? 'var(--accent)' : userAns !== undefined ? (isCorrect ? 'rgba(74,222,128,0.4)' : 'rgba(248,113,113,0.4)') : 'var(--border)'}`,
                        backgroundColor: isActive ? 'var(--bg-card2)' : userAns !== undefined ? (isCorrect ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)') : 'var(--bg-card)',
                      }}>
                      {isActive && inputVal !== '' ? (
                        <span className="text-lg font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent)' }}>{inputVal}</span>
                      ) : userAns !== undefined ? (
                        <span className="text-base font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: isCorrect ? 'var(--success)' : 'var(--danger)' }}>{userAns}</span>
                      ) : isActive ? (
                        <span className="w-4 h-0.5 animate-pulse" style={{ backgroundColor: 'var(--accent)' }}/>
                      ) : null}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          // Format Horizontal
          <div className="space-y-2 max-w-sm mx-auto">
            {Array.from({ length: ANSWERS_PER_COL }).map((_, ansIdx) => {
              const ri = ANSWERS_PER_COL - 1 - ansIdx;
              const isActive = ri === currentRow;
              const userAns = colAnswers[ri];
              const rowActual = ansIdx; // pasangan digit yang sebenarnya untuk slot penyimpanan ri
              const correctAns = (col[rowActual] + col[rowActual + 1]) % 10;
              const isCorrect = userAns === correctAns;

              return (
                <div key={ansIdx} ref={isActive ? activeRef : null}
                  className="flex items-center justify-between px-4 py-2 rounded-xl"
                  style={{
                    backgroundColor: isActive ? 'var(--bg-card2)' : 'var(--bg-card)',
                    border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                  }}>
                  <span className="text-xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-primary)' }}>
                    {col[rowActual]}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>+</span>
                  <span className="text-xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-primary)' }}>
                    {col[rowActual + 1]}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>=</span>
                  <div className="w-10 h-8 flex items-center justify-center rounded"
                       style={{ backgroundColor: 'var(--bg-card3)', border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}` }}>
                    {isActive && inputVal ? (
                      <span className="font-bold" style={{ color: 'var(--accent)', fontFamily: "'JetBrains Mono', monospace" }}>{inputVal}</span>
                    ) : userAns !== undefined ? (
                      <span className="font-bold" style={{ color: isCorrect ? 'var(--success)' : 'var(--danger)', fontFamily: "'JetBrains Mono', monospace" }}>{userAns}</span>
                    ) : <span className="w-3 h-0.5" style={{ backgroundColor: 'var(--text-faint)' }}/>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Numpad */}
      <div className="flex-shrink-0 px-4 pt-3 pb-4"
           style={{ backgroundColor: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
        <Numpad layout={layoutKeyboard} onPress={handleNumpad} />
      </div>
    </div>
  );
}

// ─── Kartu Tempo Kerja (rasio terhadap standar baku 182/3 menit) ──
function KartuTempoKerja({ rasioTempo, tempoPerTigaMenit, waktuMenit }) {
  const kategoriTempo = (rasio) => {
    if (rasio >= 100) return { label: 'Sesuai/Melampaui Standar Baku', color: 'var(--success)' };
    if (rasio >= 80) return { label: 'Mendekati Standar', color: '#facc15' };
    if (rasio >= 60) return { label: 'Perlu Ditingkatkan', color: '#fb923c' };
    return { label: 'Jauh di Bawah Standar', color: 'var(--danger)' };
  };
  if (rasioTempo === undefined || rasioTempo === null) return null;
  const infoTempo = kategoriTempo(rasioTempo);
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card2)', border: `1px solid ${infoTempo.color}` }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>⏱ Tempo Kerja</p>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${infoTempo.color}22`, color: infoTempo.color }}>
          {infoTempo.label}
        </span>
      </div>
      <p className="text-3xl font-extrabold mb-1" style={{ color: infoTempo.color, fontFamily: "'JetBrains Mono', monospace" }}>
        {rasioTempo}%
      </p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Standar baku: minimal {STANDAR_BAKU_PER_3_MENIT} hitungan / 3 menit — kamu rata-rata mencapai <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{tempoPerTigaMenit}</span> / 3 menit (dinormalisasi dari durasi {waktuMenit} menit).
      </p>
      <div className="w-full rounded-full h-2 mt-3" style={{ backgroundColor: 'var(--bg-card3)' }}>
        <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(100, rasioTempo)}%`, backgroundColor: infoTempo.color }}/>
      </div>
    </div>
  );
}

// ─── Grafik kecepatan kerja tiap checkpoint 3 menit (+ rincian per segmen) ──
// Dipakai baik di layar hasil (langsung setelah tes selesai) MAUPUN di layar
// Statistik/laporan perorangan (riwayat sesi-sesi sebelumnya), supaya user bisa
// melihat lagi kapan pun grafik kenaikan/penurunan tiap 3 menit dari sesi manapun.
function GrafikKecepatanCheckpoint({ grafikProgress }) {
  const punyaGrafik = grafikProgress && grafikProgress.length >= 2;

  // Rincian PER SEGMEN 3 menit (bukan kumulatif): selisih antar checkpoint berurutan,
  // supaya untuk tes 60 menit muncul 20 titik dengan jumlah/benar/salah masing-masing segmen.
  const segmenGrafik = punyaGrafik
    ? grafikProgress.map((g, i) => {
        const prev = i === 0 ? { jumlah: 0, benar: 0, salah: 0, menit: 0 } : grafikProgress[i - 1];
        const jumlahSegmen = g.jumlah - prev.jumlah;
        const benarSegmen = (g.benar ?? 0) - (prev.benar ?? 0);
        const salahSegmen = (g.salah ?? 0) - (prev.salah ?? 0);
        const rasioSegmen = Math.round((jumlahSegmen / STANDAR_BAKU_PER_3_MENIT) * 100);
        return { menitAwal: prev.menit, menitAkhir: g.menit, jumlahSegmen, benarSegmen, salahSegmen, rasioSegmen };
      })
    : [];

  if (!punyaGrafik) {
    return (
      <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
        Sesi ini belum punya data checkpoint tiap 3 menit (durasi terlalu singkat, atau sesi dari sebelum fitur ini ditambahkan).
      </p>
    );
  }

  // ── Grafik Kestabilan: jumlah jawaban BENAR tiap segmen 3 menit (bukan kumulatif) ──
  // Ini yang menunjukkan naik-turunnya performa per "ketukan" 3 menit sepanjang tes.
  const GW = 320, GH = 190, PAD_L = 30, PAD_R = 12, PAD_T = 14, PAD_B = 22;
  const plotW = GW - PAD_L - PAD_R;
  const plotH = GH - PAD_T - PAD_B;
  const nilaiBenar = segmenGrafik.map(s => s.benarSegmen);
  const nilaiMaksMentah = Math.max(...nilaiBenar, 1);
  const sumbuMaks = Math.max(5, Math.ceil(nilaiMaksMentah / 5) * 5);
  const jumlahGrid = 4; // 5 garis horizontal: 0, 1/4, 2/4, 3/4, penuh
  const titikX = (i) => PAD_L + (segmenGrafik.length > 1 ? (i / (segmenGrafik.length - 1)) * plotW : plotW / 2);
  const titikY = (v) => PAD_T + plotH - (v / sumbuMaks) * plotH;
  const garisPoints = nilaiBenar.map((v, i) => `${titikX(i).toFixed(1)},${titikY(v).toFixed(1)}`).join(' ');
  const areaPoints = `${titikX(0).toFixed(1)},${titikY(0).toFixed(1)} ${garisPoints} ${titikX(nilaiBenar.length - 1).toFixed(1)},${titikY(0).toFixed(1)}`;

  return (
    <>
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'var(--accent)' }}>
          📈 Grafik Kestabilan
        </p>
        <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
          Jawaban Benar per 3 Menit (Maks: {sumbuMaks})
        </p>
        <svg viewBox={`0 0 ${GW} ${GH}`} className="w-full" style={{ height: 190 }}>
          {/* Grid horizontal + label sumbu-Y */}
          {Array.from({ length: jumlahGrid + 1 }).map((_, gi) => {
            const nilaiGrid = Math.round((sumbuMaks / jumlahGrid) * gi);
            const y = titikY(nilaiGrid);
            return (
              <React.Fragment key={gi}>
                <line x1={PAD_L} y1={y} x2={GW - PAD_R} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray={gi === 0 ? '0' : '3,3'} />
                <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="8" fill="var(--text-muted)">{nilaiGrid}</text>
              </React.Fragment>
            );
          })}
          {/* Area terisi di bawah garis */}
          <polygon points={areaPoints} fill="var(--success)" fillOpacity="0.15" stroke="none" />
          {/* Garis utama */}
          <polyline points={garisPoints} fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Titik-titik */}
          {nilaiBenar.map((v, i) => (
            <circle key={i} cx={titikX(i).toFixed(1)} cy={titikY(v).toFixed(1)} r="3.5" fill="var(--bg-card2)" stroke="var(--success)" strokeWidth="2" />
          ))}
        </svg>
        <div className="flex justify-between mt-0.5 px-1">
          {segmenGrafik.map((s, i) => (
            (i === 0 || i === segmenGrafik.length - 1 || i % Math.ceil(segmenGrafik.length / 6) === 0) ? (
              <span key={i} className="text-[8px]" style={{ color: 'var(--text-muted)' }}>{s.menitAkhir}m</span>
            ) : <span key={i} />
          ))}
        </div>
        <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
          Garis stabil/naik = performa konsisten. Turun tajam = mulai melambat/lelah di periode itu.
        </p>
      </div>

      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
          Rincian per 3 Menit ({segmenGrafik.length} titik)
        </p>
        <div className="space-y-1.5">
          {segmenGrafik.map((s, i) => {
            const warnaSegmen = s.rasioSegmen >= 100 ? 'var(--success)' : s.rasioSegmen >= 80 ? '#facc15' : s.rasioSegmen >= 60 ? '#fb923c' : 'var(--danger)';
            return (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--bg-card3)' }}>
                <span className="text-[11px] font-mono w-14 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                  {s.menitAwal}-{s.menitAkhir}'
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>
                  {s.jumlahSegmen} soal
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--success)' }}>
                  ✓{s.benarSegmen}
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--danger)' }}>
                  ✗{s.salahSegmen}
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${warnaSegmen}22`, color: warnaSegmen }}>
                  {s.rasioSegmen}%
                </span>
              </div>
              );
            })}
          </div>
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
            % dihitung dari nilai baku {STANDAR_BAKU_PER_3_MENIT} hitungan/3 menit. Segmen di bawah 100% menandakan tempo melambat di periode itu.
          </p>
        </div>
    </>
  );
}

// ─── Layar Hasil ──────────────────────────────────────────────
function LayarHasil({ hasil, onUlang, onBack, sembunyikanUlang }) {
  const { totalBenar, totalDikerjakan, akurasi, waktuMenit, grafikProgress, rasioTempo, tempoPerTigaMenit } = hasil;
  const kategori = akurasi >= 85 ? 'Luar Biasa' : akurasi >= 70 ? 'Baik' : akurasi >= 55 ? 'Cukup' : 'Perlu Latihan';

  return (
    <div className="flex flex-col h-full overflow-y-auto p-5 space-y-4"
         style={{ backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' }}>
      <div className="rounded-2xl p-6 text-center"
           style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <p className="text-4xl mb-3">{akurasi >= 70 ? '🏆' : akurasi >= 55 ? '👍' : '📚'}</p>
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Hasil Kraepelin / Pauli</h2>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Durasi: {waktuMenit} menit</p>
        <p className="text-6xl font-extrabold mb-1" style={{ color: 'var(--accent)', fontFamily: "'JetBrains Mono', monospace" }}>{akurasi}%</p>
        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Akurasi — {kategori}</p>
        <div className="w-full rounded-full h-3 mb-4" style={{ backgroundColor: 'var(--bg-card3)' }}>
          <div className="h-3 rounded-full transition-all" style={{ width: `${akurasi}%`, backgroundColor: 'var(--accent)' }}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl py-3" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <p className="text-2xl font-extrabold" style={{ color: 'var(--success)', fontFamily: "'JetBrains Mono', monospace" }}>{totalBenar}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Benar</p>
          </div>
          <div className="rounded-xl py-3" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <p className="text-2xl font-extrabold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>{totalDikerjakan}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Dikerjakan</p>
          </div>
        </div>
      </div>

      <KartuTempoKerja rasioTempo={rasioTempo} tempoPerTigaMenit={tempoPerTigaMenit} waktuMenit={waktuMenit} />
      <GrafikKecepatanCheckpoint grafikProgress={grafikProgress} />

      <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        💡 Hasil ini tersimpan otomatis di statistik riwayat
      </p>
      <div className="space-y-2">
        {!sembunyikanUlang && (
          <button onClick={onUlang}
            className="w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wide"
            style={{ backgroundColor: 'var(--accent)', color: '#1B2017', fontFamily: "'Poppins', sans-serif" }}>
            🔄 Ulangi Tes
          </button>
        )}
        <button onClick={onBack}
          className="w-full py-3 rounded-xl font-semibold text-sm"
          style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
          {sembunyikanUlang ? '✓ Selesai — Kembali ke Tryout' : '← Kembali ke Menu'}
        </button>
      </div>
    </div>
  );
}

// Setting yang dipakai kalau modul ini dibuka dari alur Tryout resmi —
// durasi & format tesnya baku, tidak boleh diatur bebas oleh peserta.
const SETTING_TRYOUT = { waktuMenit: 60, tipeSoal: 'acak', formatSoal: 'horizontal', layoutKeyboard: 'grid' };

// ─── Komponen Utama ───────────────────────────────────────────
export default function KraepelinModul({ onBack, tryoutMode, tryoutId }) {
  // Snapshot progres tersimpan sekali di awal (mount) — dipakai untuk tawaran
  // "Lanjutkan" (mode latihan) atau langsung dipakai (mode Tryout, tak ada
  // layar pengaturan untuk menampilkan tombolnya).
  const [progresAwal, setProgresAwal] = useState(() => bacaProgresTes('kraepelin', tryoutId));
  const [sedangResume, setSedangResume] = useState(() => tryoutMode && !!progresAwal);
  const [fase, setFase]           = useState(tryoutMode ? 'tes' : 'pengaturan');
  const [pengaturan, setPengaturan] = useState(() => {
    if (tryoutMode) return progresAwal ? { ...progresAwal.pengaturan, formatSoal: SETTING_TRYOUT.formatSoal } : SETTING_TRYOUT;
    return null;
  });
  const [hasil, setHasil]         = useState(null);

  // Cegah keluar tanpa sengaja (tombol back/gesture HP) selagi sedang mengerjakan
  useCegahKeluarTes(fase === 'tes', onBack);

  const handleMulai = (setting) => {
    hapusProgresTes('kraepelin', tryoutId);
    setProgresAwal(null);
    setSedangResume(false);
    setPengaturan(setting);
    setFase('tes');
  };

  const handleLanjutkan = () => {
    if (!progresAwal) return;
    setPengaturan(progresAwal.pengaturan);
    setSedangResume(true);
    setFase('tes');
  };

  const handleMulaiBaruDariBanner = () => {
    hapusProgresTes('kraepelin', tryoutId);
    setProgresAwal(null);
  };

  const handleSelesai = (hasilData) => {
    setHasil(hasilData);
    setFase('hasil');
  };

  const handleUlang = () => {
    setFase('pengaturan');
    setPengaturan(null);
    setHasil(null);
  };

  if (fase === 'pengaturan') return (
    <LayarPengaturan
      onMulai={handleMulai}
      onBack={onBack}
      onLihatStatistik={() => setFase('statistik')}
      progresAwal={progresAwal}
      onLanjutkan={handleLanjutkan}
      onMulaiBaru={handleMulaiBaruDariBanner}
    />
  );

  if (fase === 'statistik') return (
    <LayarStatistik onBack={() => setFase('pengaturan')} />
  );

  if (fase === 'tes' && pengaturan) return (
    <LayarTes
      pengaturan={pengaturan}
      onSelesai={handleSelesai}
      onBack={() => setFase('pengaturan')}
      tryoutMode={tryoutMode}
      tryoutId={tryoutId}
      resumeData={sedangResume ? progresAwal : null}
    />
  );

  if (fase === 'hasil' && hasil) return (
    <LayarHasil
      hasil={hasil}
      onUlang={handleUlang}
      onBack={onBack}
      sembunyikanUlang={tryoutMode}
    />
  );

  return null;
}
